import { useEffect, useState } from "react";
import { Check, Loader2, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import { Card } from "./VerifyPromoSettings";

const today = () => new Date().toISOString().slice(0, 10);
const STATUSES = [["awaiting_payment", "Awaiting payment"], ["approved", "Approved"], ["declined", "Declined"], ["cancelled", "Cancelled"]];

const RequestRow = ({ r, onDone }) => {
  const [start, setStart] = useState(today());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState("");
  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "approve") { const { data } = await api.post(`/staff/feature-requests/${r.id}/approve`, { start }); toast.success(`Featured ${data.start} → ${data.end}`); }
      else { await api.post(`/staff/feature-requests/${r.id}/decline`, { reason }); toast.success("Request declined"); }
      onDone();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid={`feature-req-${r.id}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-mono font-bold text-[#12234A]" data-testid={`feature-req-ref-${r.id}`}>{r.ref}</span>
        <a href={`/listing/${r.listing_id}`} target="_blank" rel="noreferrer" className="font-semibold text-[#F5551E] hover:underline truncate max-w-xs">{r.listing_title}</a>
        <span className="text-sm text-slate-600">{r.package_name} · {r.days} days · <strong>${Number(r.price).toFixed(2)}</strong></span>
        <span className="text-xs text-slate-400">{r.user_name} · {r.user_email} · {r.created_at?.slice(0, 10)}</span>
      </div>
      {r.status === "awaiting_payment" ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label className="text-xs font-semibold text-slate-500 flex items-center gap-2">Start date
            <input type="date" min={today()} value={start} onChange={(e) => setStart(e.target.value)} className="h-9 rounded-lg border border-slate-200 px-2 text-sm" data-testid={`feature-req-start-${r.id}`} /></label>
          <Button onClick={() => act("approve")} disabled={!!busy} className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" data-testid={`feature-req-approve-${r.id}`}>
            {busy === "approve" ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Payment received — feature it</Button>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" className="h-9 w-48 rounded-lg" data-testid={`feature-req-reason-${r.id}`} />
          <Button variant="outline" onClick={() => act("decline")} disabled={!!busy} className="h-9 rounded-lg gap-1.5 text-red-600" data-testid={`feature-req-decline-${r.id}`}><X size={14} /> Decline</Button>
        </div>
      ) : (
        <p className="mt-2 text-xs text-slate-500">{r.status === "approved" ? `Featured ${r.start} → ${r.end}` : r.status}{r.reason ? ` · ${r.reason}` : ""}{r.closed_by ? ` · by ${r.closed_by}` : ""}</p>
      )}
    </div>
  );
};

export function PaidFeaturedAdmin() {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState("awaiting_payment");
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/staff/feature-requests", { params: { status } }).then(({ data: d }) => setData(d)).catch((e) => toast.error(errText(e)));
  useEffect(() => { api.get("/admin/paid-featured").then(({ data: d }) => setCfg(d)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const setPkg = (i, k, v) => set("packages", cfg.packages.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const save = async () => {
    setSaving(true);
    try { const { data: d } = await api.put("/admin/paid-featured", { ...cfg, packages: cfg.packages.map((p) => ({ ...p, days: Number(p.days), price: Number(p.price) })) }); setCfg(d); toast.success("Paid featured settings saved"); }
    catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-5" data-testid="paid-featured-admin">
      <Card title="Paid featured requests">
        <div className="flex flex-wrap gap-2 mb-4">
          {STATUSES.map(([v, l]) => (
            <button key={v} type="button" onClick={() => setStatus(v)} data-testid={`feature-req-filter-${v}`}
              className={`h-8 px-3 rounded-full text-xs font-semibold border ${status === v ? "bg-[#12234A] text-white border-[#12234A]" : "bg-white text-[#12234A] border-slate-200"}`}>
              {l} <span className="opacity-60">{data?.counts?.[v] ?? ""}</span></button>
          ))}
        </div>
        {!data ? <Loader2 className="animate-spin text-slate-300" /> : data.items.length ? (
          <div className="space-y-3">{data.items.map((r) => <RequestRow key={r.id} r={r} onDone={load} />)}</div>
        ) : <p className="text-sm text-slate-400" data-testid="feature-req-empty">Nothing here.</p>}
      </Card>

      {cfg && (
        <Card title="Paid featured settings (Interac e-Transfer)">
          <div className="space-y-4">
            <label className="flex items-center gap-3"><Switch checked={cfg.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="paid-feat-enabled" />
              <span className="text-sm font-semibold text-[#12234A]">Let sellers buy featured spots from their ad page</span></label>
            <label className="block text-xs font-semibold text-slate-500">Email that receives e-Transfers
              <Input value={cfg.emt_email} onChange={(e) => set("emt_email", e.target.value)} placeholder="payments@critterposts.ca" className="mt-1 max-w-sm" data-testid="paid-feat-email" /></label>
            <div className="grid md:grid-cols-2 gap-3">
              {["en", "fr"].map((l) => (
                <label key={l} className="block text-xs font-semibold text-slate-500">Payment instructions ({l === "en" ? "English" : "French"})
                  <Textarea rows={3} value={cfg[`instructions_${l}`]} onChange={(e) => set(`instructions_${l}`, e.target.value)} className="mt-1" data-testid={`paid-feat-ins-${l}`} /></label>
              ))}
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">Packages (CAD)</p>
              <div className="space-y-2">
                {cfg.packages.map((p, i) => (
                  <div key={p.id || i} className="grid grid-cols-[1fr_1fr_80px_100px_36px] gap-2 items-center" data-testid={`paid-feat-pkg-${i}`}>
                    <Input value={p.name_en} onChange={(e) => setPkg(i, "name_en", e.target.value)} placeholder="Name (English)" data-testid={`paid-feat-pkg-${i}-name-en`} />
                    <Input value={p.name_fr} onChange={(e) => setPkg(i, "name_fr", e.target.value)} placeholder="Name (French)" data-testid={`paid-feat-pkg-${i}-name-fr`} />
                    <Input type="number" min={1} max={365} value={p.days} onChange={(e) => setPkg(i, "days", e.target.value)} placeholder="Days" data-testid={`paid-feat-pkg-${i}-days`} />
                    <Input type="number" min={0} step="0.01" value={p.price} onChange={(e) => setPkg(i, "price", e.target.value)} placeholder="Price" data-testid={`paid-feat-pkg-${i}-price`} />
                    <button type="button" onClick={() => set("packages", cfg.packages.filter((_, j) => j !== i))} disabled={cfg.packages.length <= 1} className="h-9 w-9 rounded-lg border text-red-500 flex items-center justify-center disabled:opacity-30" aria-label="Remove package" data-testid={`paid-feat-pkg-${i}-remove`}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
              {cfg.packages.length < 6 && <button type="button" onClick={() => set("packages", [...cfg.packages, { id: "", name_en: "", name_fr: "", days: 14, price: 14.99 }])} className="mt-2 text-sm font-semibold text-[#F5551E] inline-flex items-center gap-1" data-testid="paid-feat-pkg-add"><Plus size={14} /> Add package</button>}
            </div>
            <Button onClick={save} disabled={saving} className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-2" data-testid="paid-feat-save">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
